import React from "react";
import ReactDOM from "react-dom/client";
import "./index.css";
import App from "./App";
import reportWebVitals from "./reportWebVitals";
import { Provider } from "react-redux";
import store from "./redux/store";
import { HelmetProvider } from "react-helmet-async";
import * as Sentry from "@sentry/react";
import { initRum } from "./observability/rum";

initRum();

const envMode = process.env.REACT_APP_ENV;

const shouldEnableSentry = ["prod", "test", "dev"].includes(envMode as string);

let sentryInitialized = false;
let lastRestartAt = 0;

function restartSentrySession(reason: string) {
  if (!sentryInitialized) return;

  const now = Date.now();

  // Prevent restart spam (5s debounce)
  if (now - lastRestartAt < 5000) return;
  lastRestartAt = now;

  try {
    Sentry.endSession();
    Sentry.startSession();

    Sentry.setContext("session_restart", {
      reason: reason,
      timestamp: new Date().toISOString(),
    });

    Sentry.flush(5000);
  } catch (e) {
    console.warn("[Sentry] Failed to restart session", e);
  }
}

if (shouldEnableSentry) {
  const sentryConfig: Parameters<typeof Sentry.init>[0] & { enableLogs?: boolean } = {
    dsn: "https://662c1f8081215d8513fd4e66ada6207b@o4506863228616704.ingest.us.sentry.io/4511568615047168",

    release: process.env.REACT_APP_VERSION,
    environment: envMode,

    autoSessionTracking: true,

    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
        networkDetailAllowUrls: [
          /^https:\/\/dev\.api\.incruiter\.com/,
          /^https:\/\/test\.api\.incruiter\.com/,
          /^https:\/\/api\.incruiter\.com/,
          /^https:\/\/.*\.incruiter\.com/,
        ],
        networkRequestHeaders: ["Authorization", "Content-Type"],
        networkResponseHeaders: ["Content-Type"],
      }),
      Sentry.replayCanvasIntegration(),
    ],

    transport: Sentry.makeBrowserOfflineTransport(Sentry.makeFetchTransport),

    tracesSampleRate: envMode === "prod" ? 0.1 : 1.0,
    replaysSessionSampleRate: envMode === "prod" ? 0.1 : 1.0,
    replaysOnErrorSampleRate: 1.0,

    tracePropagationTargets: ["localhost", /^https:\/\/.*\.incruiter\.com/],
    enableLogs: true,
  };
  Sentry.init(sentryConfig);

  sentryInitialized = true;
  Sentry.setTag("env", envMode);

  window.addEventListener("online", function () {
    restartSentrySession("network_online");
  });

  document.addEventListener("visibilitychange", function () {
    if (document.visibilityState === "visible") {
      restartSentrySession("tab_visible");
    }
  });
}

const root = ReactDOM.createRoot(document.getElementById("root") as HTMLElement);
root.render(
  <React.StrictMode>
    <HelmetProvider>
      <Provider store={store}>
        <App />
      </Provider>
    </HelmetProvider>
  </React.StrictMode>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
